import * as THREE from 'three';
import {
  buildDatumReferenceEntityId,
  DatumReferenceEntityKind,
} from '../../reference/DatumReferenceEntityId';
import {
  type DatumAxis,
  getDatumAxisIdFromObject,
} from '../../reference/DatumAxis';
import {
  type DatumPlane,
  getDatumPlaneIdFromObject,
} from '../../reference/DatumPlane';
import {
  type DatumPoint,
  getDatumPointIdFromObject,
  isDatumPoint,
} from '../../reference/DatumPoint';
import {
  cloneAssemblyReferenceDependencySnapshot,
  type AssemblyReferenceDependencySnapshot,
} from '../../assembly/definition';

/**
 * 可解析装配体基准引用的宿主。
 *
 * 设计说明：
 * 选择层只需要按稳定 ID 取回当前正式基准实体，避免把 Mate 逻辑耦合到具体 Viewer class。
 */
export interface AssemblyReferenceSelectionHost {
  /** 按稳定 ID 读取基准面。 */
  getDatumPlane(planeId: number): DatumPlane | undefined | null;
  /** 按稳定 ID 读取基准轴。 */
  getDatumAxis(axisId: number): DatumAxis | undefined | null;
  /** 按稳定 ID 读取基准点。 */
  getDatumPoint(pointId: number): DatumPoint | undefined | null;
  /** 解析基准引用与其创建参考对象之间的装配体依赖。 */
  resolveAssemblyReferenceDependency?(
    referenceRef: AssemblyReferenceSelectionRef,
  ): AssemblyReferenceDependencySnapshot | null;
}

/** 基准引用虚拟组件 ID 前缀集合。 */
const ASSEMBLY_REFERENCE_COMPONENT_PREFIXES = [
  `${DatumReferenceEntityKind.Plane}:`,
  `${DatumReferenceEntityKind.Axis}:`,
  `${DatumReferenceEntityKind.Point}:`,
] as const;

/** 世界 X 轴。 */
const WORLD_X_AXIS: TopologyPoint3D = [1, 0, 0];

/** 世界 Y 轴。 */
const WORLD_Y_AXIS: TopologyPoint3D = [0, 1, 0];

/** 世界 Z 轴。 */
const WORLD_Z_AXIS: TopologyPoint3D = [0, 0, 1];

/**
 * 将 Three.js 向量转换为可序列化三维点。
 *
 * @param value Three.js 向量。
 * @returns 可序列化三维点。
 */
function toTopologyPoint(value: THREE.Vector3): TopologyPoint3D {
  return [value.x, value.y, value.z];
}

/**
 * 复制可序列化三维点。
 *
 * @param value 源三维点。
 * @returns 独立三维点数组。
 */
function cloneTopologyPoint(value: TopologyPoint3D): TopologyPoint3D {
  return [value[0], value[1], value[2]];
}

/**
 * 从宿主读取基准依赖快照。
 *
 * @param host 基准引用宿主。
 * @param referenceRef 基准稳定引用。
 * @returns 依赖快照副本；默认固定基准或宿主未实现时返回 `undefined`。
 */
function resolveReferenceDependency(
  host: AssemblyReferenceSelectionHost,
  referenceRef: AssemblyReferenceSelectionRef,
): AssemblyReferenceDependencySnapshot | undefined {
  if (typeof host.resolveAssemblyReferenceDependency !== 'function') {
    return undefined;
  }
  return cloneAssemblyReferenceDependencySnapshot(
    host.resolveAssemblyReferenceDependency(referenceRef),
  );
}

/**
 * 规范化向量并转换为可序列化三维点。
 *
 * @param value 待转换向量。
 * @param fallback 退化时使用的兜底方向。
 * @returns 可序列化单位方向。
 */
function toNormalizedTopologyPoint(
  value: THREE.Vector3,
  fallback: TopologyPoint3D,
): TopologyPoint3D {
  const normalized = value.clone();
  if (normalized.lengthSq() <= 1e-12) {
    return cloneTopologyPoint(fallback);
  }
  normalized.normalize();
  return toTopologyPoint(normalized);
}

/**
 * 创建稳定基准引用。
 *
 * @param kind 基准引用类型。
 * @param referenceId 基准实体稳定 ID。
 * @returns 选择层稳定引用。
 */
function createAssemblyReferenceSelectionRef(
  kind: DatumReferenceEntityKind,
  referenceId: number,
): AssemblyReferenceSelectionRef {
  return {
    schemaVersion: 1,
    kind,
    referenceId,
    stableRef: buildDatumReferenceEntityId(kind, referenceId),
  };
}

/**
 * 判断组件 ID 是否代表装配体基准引用虚拟组件。
 *
 * 设计说明：
 * 基准点/轴/面在 SOLIDWORKS 语义下是装配体参考对象，本身不应被 Mate 求解器移动；
 * 因此进入求解器时用固定的虚拟组件状态表达其“不可移动”边界。
 *
 * @param value 待判断组件 ID。
 * @returns 是否为基准引用虚拟组件 ID。
 */
export function isAssemblyReferenceSelectionComponentInstanceId(
  value: string | undefined | null,
): value is string {
  return typeof value === 'string'
    && ASSEMBLY_REFERENCE_COMPONENT_PREFIXES.some((prefix) => value.startsWith(prefix));
}

/**
 * 解析基准面选择快照。
 *
 * @param object 本次命中的对象。
 * @param host 基准引用宿主。
 * @returns 基准面快照；不匹配时返回 `null`。
 */
function resolveDatumPlaneSelectionSnapshot(
  object: THREE.Object3D,
  host: AssemblyReferenceSelectionHost,
): AssemblyReferenceSelectionSnapshotEntry | null {
  const datumPlaneId = getDatumPlaneIdFromObject(object);
  if (datumPlaneId === null) {
    return null;
  }

  const datumPlane = typeof host.getDatumPlane === 'function'
    ? host.getDatumPlane(datumPlaneId)
    : null;
  if (!datumPlane) {
    return null;
  }

  const referenceRef = createAssemblyReferenceSelectionRef(DatumReferenceEntityKind.Plane, datumPlane.id);
  const descriptor: BRepReferencePlaneGeometryDescriptor = {
    entityKind: 'referencePlane',
    bodyObjectId: referenceRef.stableRef,
    topologyIndex: datumPlane.id,
    subEntityId: referenceRef.stableRef,
    persistentRef: referenceRef.stableRef,
    coordinateSpace: 'world',
    point: toTopologyPoint(datumPlane.originWorld),
    normal: toNormalizedTopologyPoint(datumPlane.normal, WORLD_Z_AXIS),
  };

  return {
    objectId: object.uuid,
    referenceRef,
    referenceDependency: resolveReferenceDependency(host, referenceRef),
    brepGeometryDescriptor: descriptor,
  };
}

/**
 * 解析基准轴选择快照。
 *
 * @param object 本次命中的对象。
 * @param host 基准引用宿主。
 * @returns 基准轴快照；不匹配时返回 `null`。
 */
function resolveDatumAxisSelectionSnapshot(
  object: THREE.Object3D,
  host: AssemblyReferenceSelectionHost,
): AssemblyReferenceSelectionSnapshotEntry | null {
  const datumAxisId = getDatumAxisIdFromObject(object);
  if (datumAxisId === null) {
    return null;
  }

  const datumAxis = typeof host.getDatumAxis === 'function'
    ? host.getDatumAxis(datumAxisId)
    : null;
  if (!datumAxis) {
    return null;
  }

  const referenceRef = createAssemblyReferenceSelectionRef(DatumReferenceEntityKind.Axis, datumAxis.datumAxisId);
  const descriptor: BRepReferenceAxisGeometryDescriptor = {
    entityKind: 'referenceAxis',
    bodyObjectId: referenceRef.stableRef,
    topologyIndex: datumAxis.datumAxisId,
    subEntityId: referenceRef.stableRef,
    persistentRef: referenceRef.stableRef,
    coordinateSpace: 'world',
    origin: toTopologyPoint(datumAxis.originWorld),
    direction: toNormalizedTopologyPoint(datumAxis.getWorldDirection(), WORLD_Z_AXIS),
  };

  return {
    objectId: object.uuid,
    referenceRef,
    referenceDependency: resolveReferenceDependency(host, referenceRef),
    brepGeometryDescriptor: descriptor,
  };
}

/**
 * 解析基准点选择快照。
 *
 * @param object 本次命中的对象。
 * @param host 基准引用宿主。
 * @returns 基准点快照；不匹配时返回 `null`。
 */
function resolveDatumPointSelectionSnapshot(
  object: THREE.Object3D,
  host: AssemblyReferenceSelectionHost,
): AssemblyReferenceSelectionSnapshotEntry | null {
  const datumPointId = getDatumPointIdFromObject(object);
  if (datumPointId === null) {
    return null;
  }

  const datumPoint = typeof host.getDatumPoint === 'function'
    ? host.getDatumPoint(datumPointId)
    : null;
  if (!datumPoint) {
    return null;
  }

  const point = toTopologyPoint(datumPoint.getWorldPoint());
  const referenceRef = createAssemblyReferenceSelectionRef(DatumReferenceEntityKind.Point, datumPoint.datumPointId);
  const descriptor: BRepOriginGeometryDescriptor | BRepVertexGeometryDescriptor = datumPoint.isOrigin
    ? {
        entityKind: 'origin',
        bodyObjectId: referenceRef.stableRef,
        topologyIndex: datumPoint.datumPointId,
        subEntityId: referenceRef.stableRef,
        persistentRef: referenceRef.stableRef,
        coordinateSpace: 'world',
        point,
        xAxis: cloneTopologyPoint(WORLD_X_AXIS),
        yAxis: cloneTopologyPoint(WORLD_Y_AXIS),
        zAxis: cloneTopologyPoint(WORLD_Z_AXIS),
      }
    : {
        entityKind: 'vertex',
        bodyObjectId: referenceRef.stableRef,
        topologyIndex: datumPoint.datumPointId,
        subEntityId: referenceRef.stableRef,
        persistentRef: referenceRef.stableRef,
        coordinateSpace: 'world',
        point,
      };

  return {
    objectId: object.uuid,
    referenceRef,
    referenceDependency: resolveReferenceDependency(host, referenceRef),
    brepGeometryDescriptor: descriptor,
  };
}

/**
 * 将本次命中的装配体基准对象转换为选择层稳定快照。
 *
 * @param object 本次命中的 Three.js 对象。
 * @param host 基准引用宿主。
 * @returns 基准选择快照；对象不是正式基准引用时返回 `null`。
 */
export function resolveAssemblyReferenceSelectionSnapshotEntry(
  object: THREE.Object3D,
  host: AssemblyReferenceSelectionHost,
): AssemblyReferenceSelectionSnapshotEntry | null {
  if (isDatumPoint(object)) {
    return resolveDatumPointSelectionSnapshot(object, host);
  }

  return resolveDatumPlaneSelectionSnapshot(object, host)
    ?? resolveDatumAxisSelectionSnapshot(object, host)
    ?? resolveDatumPointSelectionSnapshot(object, host);
}
